import { COVAL_MARK_PATHS, COVAL_MARK_VIEWBOX } from "../../covalMark"

const SVG_NAMESPACE = "http://www.w3.org/2000/svg"
const TANGENT_EPSILON = 0.5
const TAU = Math.PI * 2

/**
 * Particle attributes, kept as parallel typed arrays so the render loop never
 * allocates. Positions are pixel offsets from the centre of the field.
 */
export interface MarkParticleField {
  count: number
  /** Resting position: a point inside the mark's stroke. */
  targetX: Float32Array
  targetY: Float32Array
  /** Chaotic position, expressed as an orbit so the cloud keeps swirling. */
  orbitRadius: Float32Array
  orbitAngle: Float32Array
  orbitSpin: Float32Array
  /** Fraction of the convergence window each particle waits before homing. */
  delay: Float32Array
  /** Per-particle offset so the settling wobble never looks synchronised. */
  phase: Float32Array
}

export interface BuildMarkParticleFieldOptions {
  count: number
  /** Edge length, in pixels, of the resolved mark. */
  markSize: number
  /** Radius, in pixels, the chaotic cloud is allowed to spread across. */
  cloudRadius: number
  /**
   * Radius, in pixels, each particle is drawn at once it has landed. Sampling
   * insets by this, so the outermost particle's *edge* — not its centre — lands
   * on the edge of the stroke.
   */
  particleRadius: number
  random?: () => number
}

/**
 * Splits a particle budget across path segments in proportion to their length,
 * so a long path is not sampled at the same density as a short one. Every
 * segment keeps at least one particle and the parts always sum to `total`.
 */
export function allocateSampleCounts(
  lengths: readonly number[],
  total: number,
): number[] {
  const usable = lengths.map((length) => (length > 0 ? length : 0))
  const sum = usable.reduce((carry, length) => carry + length, 0)
  if (sum <= 0) {
    return usable.map(() => 0)
  }

  const counts = usable.map((length) =>
    length > 0 ? Math.max(1, Math.floor((length / sum) * total)) : 0,
  )
  let remainder = total - counts.reduce((carry, count) => carry + count, 0)

  // Hand any rounding slack to the longest segments first.
  const byLengthDescending = usable
    .map((length, index) => ({ index, length }))
    .filter((entry) => entry.length > 0)
    .sort((left, right) => right.length - left.length)

  let cursor = 0
  while (remainder !== 0 && byLengthDescending.length > 0) {
    const { index } = byLengthDescending[cursor % byLengthDescending.length]
    if (remainder > 0) {
      counts[index] += 1
      remainder -= 1
    } else if (counts[index] > 1) {
      counts[index] -= 1
      remainder += 1
    }
    cursor += 1
  }

  return counts
}

/**
 * An `<svg>` clips to its viewBox, and the mark's strokes overshoot it, so the
 * particles have to be clipped the same way or the cloud resolves wider than
 * the mark it hands over to.
 */
function withinViewBox(x: number, y: number) {
  return x >= 0 && x <= COVAL_MARK_VIEWBOX && y >= 0 && y <= COVAL_MARK_VIEWBOX
}

function createMeasurementHost(): SVGSVGElement | null {
  if (typeof document === "undefined") {
    return null
  }

  const svg = document.createElementNS(SVG_NAMESPACE, "svg")
  svg.setAttribute("aria-hidden", "true")
  svg.setAttribute("width", "0")
  svg.setAttribute("height", "0")
  svg.style.position = "absolute"
  svg.style.overflow = "hidden"
  svg.style.pointerEvents = "none"
  document.body.appendChild(svg)
  return svg
}

/**
 * Samples points across the mark's stroked area: evenly along each path by arc
 * length, then pushed sideways by a random fraction of the stroke width. The
 * resolved cloud therefore has the same visual weight as the rendered mark
 * rather than reading as a set of hairlines.
 *
 * The sideways push is bounded so that a particle drawn at `particleRadius`
 * still falls entirely within the stroke. Sampling the full half-width instead
 * puts the outermost particles' *centres* on the edge, and the landed cloud
 * ends up half a particle wider than the mark all the way round — which then
 * visibly contracts as the cloud fades out from under it.
 *
 * Returns `null` where path measurement is unavailable (server render, jsdom),
 * which callers treat as "skip the animation and show the static mark".
 */
export function buildMarkParticleField({
  count,
  markSize,
  cloudRadius,
  particleRadius,
  random = Math.random,
}: BuildMarkParticleFieldOptions): MarkParticleField | null {
  const host = createMeasurementHost()
  if (!host) {
    return null
  }

  try {
    const paths = COVAL_MARK_PATHS.map((markPath) => {
      const element = document.createElementNS(SVG_NAMESPACE, "path")
      element.setAttribute("d", markPath.d)
      host.appendChild(element)
      return { element, strokeWidth: markPath.strokeWidth }
    })

    if (typeof paths[0]?.element.getTotalLength !== "function") {
      return null
    }

    const lengths = paths.map(({ element }) => {
      const length = element.getTotalLength()
      return Number.isFinite(length) ? length : 0
    })
    if (lengths.every((length) => length <= 0)) {
      return null
    }

    const allocation = allocateSampleCounts(lengths, count)
    const total = allocation.reduce((carry, part) => carry + part, 0)

    const targetX = new Float32Array(total)
    const targetY = new Float32Array(total)
    const orbitRadius = new Float32Array(total)
    const orbitAngle = new Float32Array(total)
    const orbitSpin = new Float32Array(total)
    const delay = new Float32Array(total)
    const phase = new Float32Array(total)

    const scale = markSize / COVAL_MARK_VIEWBOX
    const centre = COVAL_MARK_VIEWBOX / 2
    // Sampling happens in viewBox units; the particle is sized in pixels.
    const particleRadiusInViewBox = particleRadius / scale
    let cursor = 0

    paths.forEach(({ element, strokeWidth }, pathIndex) => {
      const pathLength = lengths[pathIndex]
      const samples = allocation[pathIndex]
      const spread = Math.max(0, strokeWidth / 2 - particleRadiusInViewBox)

      for (let index = 0; index < samples; index += 1) {
        const distance = ((index + random()) / samples) * pathLength
        const before = element.getPointAtLength(
          Math.max(0, distance - TANGENT_EPSILON),
        )
        const after = element.getPointAtLength(
          Math.min(pathLength, distance + TANGENT_EPSILON),
        )
        const tangentX = after.x - before.x
        const tangentY = after.y - before.y
        const tangentLength = Math.hypot(tangentX, tangentY) || 1
        const offset = (random() * 2 - 1) * spread

        const baseX = (before.x + after.x) / 2
        const baseY = (before.y + after.y) / 2
        if (!withinViewBox(baseX, baseY)) {
          continue
        }

        let pointX = baseX + (-tangentY / tangentLength) * offset
        let pointY = baseY + (tangentX / tangentLength) * offset
        if (!withinViewBox(pointX, pointY)) {
          pointX = baseX
          pointY = baseY
        }

        targetX[cursor] = (pointX - centre) * scale
        targetY[cursor] = (pointY - centre) * scale

        // Bias the cloud outwards so it reads as a shell collapsing inwards.
        orbitRadius[cursor] = cloudRadius * (0.32 + 0.68 * Math.sqrt(random()))
        orbitAngle[cursor] = random() * TAU
        orbitSpin[cursor] = (random() * 2 - 1) * 0.0022
        delay[cursor] = random() * 0.34
        phase[cursor] = random() * TAU
        cursor += 1
      }
    })

    // `cursor` trails `total` by however many samples fell outside the viewBox.
    return {
      count: cursor,
      targetX: targetX.subarray(0, cursor),
      targetY: targetY.subarray(0, cursor),
      orbitRadius: orbitRadius.subarray(0, cursor),
      orbitAngle: orbitAngle.subarray(0, cursor),
      orbitSpin: orbitSpin.subarray(0, cursor),
      delay: delay.subarray(0, cursor),
      phase: phase.subarray(0, cursor),
    }
  } finally {
    host.remove()
  }
}
